if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.pointers = new Map();
        this.content = this.querySelector('[role="document"]');
        this.controls = this.querySelector('.product-media-modal__mobile-controls');
        if (!this.hasAttribute('data-mobile-gallery')) return;

        this.controls.querySelector('[data-media-previous]').addEventListener('click', () => this.stepMedia(-1));
        this.controls.querySelector('[data-media-next]').addEventListener('click', () => this.stepMedia(1));
        this.controls.querySelector('[data-media-reset]').addEventListener('click', () => this.resetZoom());
        // Dawn closes image modals on mouse pointerup. Keep mobile navigation open.
        this.controls.addEventListener('pointerup', (event) => {
          if (this.isMobileGallery()) event.stopPropagation();
        });
        this.content.addEventListener('pointerdown', (event) => this.startGesture(event));
        this.content.addEventListener('pointermove', (event) => this.moveGesture(event));
        this.content.addEventListener('pointerup', (event) => {
          if (this.isMobileGallery()) event.stopPropagation();
          this.endGesture(event);
        });
        this.content.addEventListener('pointercancel', (event) => this.endGesture(event, true));
        this.content.addEventListener('dragstart', (event) => {
          if (this.isMobileGallery()) event.preventDefault();
        });
        this.addEventListener('keydown', (event) => {
          if (!this.isMobileGallery() || event.target.closest('deferred-media, product-model')) return;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            this.stepMedia(event.key === 'ArrowLeft' ? -1 : 1);
          }
        });
        this.onBreakpointChange = () => {
          this.resetZoom();
          if (this.hasAttribute('open')) this.showActiveMedia();
        };
      }

      connectedCallback() {
        super.connectedCallback();
        if (this.onBreakpointChange) this.mobileQuery.addEventListener('change', this.onBreakpointChange);
      }

      disconnectedCallback() {
        if (this.onBreakpointChange) this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
      }

      isMobileGallery() {
        return this.hasAttribute('data-mobile-gallery') && this.mobileQuery.matches;
      }

      hide() {
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        if (this.isMobileGallery()) {
          // Use the current gallery, including its selected-variant filtering.
          const gallery = this.openedBy.closest('media-gallery');
          const ids = gallery ? Array.from(gallery.querySelectorAll('.product__media-item'))
            .filter((item) => !item.classList.contains('variant-media-hidden') && getComputedStyle(item).display !== 'none')
            .map((item) => item.querySelector('button[data-media-id]')?.dataset.mediaId) : [];
          const media = Array.from(this.content.children).filter((item) => item.dataset.mediaId);
          this.mobileMedia = ids.length ? ids.map((id) => media.find((item) => item.dataset.mediaId === id)).filter(Boolean) : media;
          this.mediaIndex = Math.max(0, this.mobileMedia.findIndex((item) => item.dataset.mediaId === this.openedBy.dataset.mediaId));
          this.renderMobileMedia();
          return;
        }
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      stepMedia(direction) {
        if (!this.isMobileGallery() || !this.mobileMedia?.length) return;
        const index = Math.max(0, Math.min(this.mobileMedia.length - 1, this.mediaIndex + direction));
        if (index === this.mediaIndex) return;
        window.pauseAllMedia();
        this.mediaIndex = index;
        this.renderMobileMedia();
      }

      renderMobileMedia() {
        this.resetZoom();
        this.activeMobileMedia = this.mobileMedia[this.mediaIndex];
        if (!this.activeMobileMedia) return;
        Array.from(this.content.children).forEach((item) => item.classList.toggle('active', item === this.activeMobileMedia));
        this.content.scrollTop = 0;
        this.content.scrollLeft = 0;
        this.controls.querySelector('[data-media-counter]').textContent = `${this.mediaIndex + 1} / ${this.mobileMedia.length}`;
        this.controls.querySelector('[data-media-previous]').disabled = this.mediaIndex === 0;
        this.controls.querySelector('[data-media-next]').disabled = this.mediaIndex === this.mobileMedia.length - 1;
        if (this.activeMobileMedia.nodeName === 'DEFERRED-MEDIA' &&
            this.activeMobileMedia.querySelector('template')?.content.querySelector('.js-youtube')) {
          this.activeMobileMedia.loadContent();
        }
      }

      resetZoom() {
        if (this.activeMobileMedia) this.activeMobileMedia.style.removeProperty('transform');
        this.scale = 1;
        this.pan = { x: 0, y: 0 };
        this.pointers.clear();
        this.gesture = null;
        if (this.controls) this.controls.querySelector('[data-media-reset]').hidden = true;
      }

      startGesture(event) {
        if (!this.isMobileGallery() || this.activeMobileMedia?.nodeName !== 'IMG' ||
            event.target !== this.activeMobileMedia || (event.pointerType === 'mouse' && event.button !== 0)) return;
        event.preventDefault();
        this.content.setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        this.beginGesture();
      }

      beginGesture() {
        const points = Array.from(this.pointers.values());
        if (!points.length) { this.gesture = null; return; }
        const center = points.length > 1 ? {
          x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2,
        } : points[0];
        this.gesture = {
          center, scale: this.scale, pan: { ...this.pan },
          distance: points.length > 1 ? Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y) : 0,
          pinched: points.length > 1 || Boolean(this.gesture?.pinched),
        };
      }

      moveGesture(event) {
        if (!this.pointers.has(event.pointerId) || !this.gesture) return;
        event.preventDefault();
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        const points = Array.from(this.pointers.values());
        const start = this.gesture;
        if (points.length > 1 && start.distance > 0) {
          const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
          this.scale = Math.max(1, Math.min(4, start.scale * distance / start.distance));
          const bounds = this.activeMobileMedia.getBoundingClientRect();
          const center = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
          // Zoom around the fingers, not an unrelated corner of the photo.
          const origin = { x: bounds.x + bounds.width / 2 - this.pan.x, y: bounds.y + bounds.height / 2 - this.pan.y };
          const ratio = this.scale / start.scale;
          this.pan = {
            x: center.x - origin.x - (start.center.x - origin.x - start.pan.x) * ratio,
            y: center.y - origin.y - (start.center.y - origin.y - start.pan.y) * ratio,
          };
        } else if (this.scale > 1) {
          this.pan = { x: start.pan.x + event.clientX - start.center.x, y: start.pan.y + event.clientY - start.center.y };
        }
        this.applyZoom();
      }

      applyZoom() {
        const image = this.activeMobileMedia;
        if (!image || image.nodeName !== 'IMG') return;
        // Bound panning by the actual contained photo, including non-square images.
        const fit = Math.min(image.clientWidth / (image.naturalWidth || image.clientWidth), image.clientHeight / (image.naturalHeight || image.clientHeight));
        const maxX = Math.max(0, ((image.naturalWidth || image.clientWidth) * fit * this.scale - image.clientWidth) / 2);
        const maxY = Math.max(0, ((image.naturalHeight || image.clientHeight) * fit * this.scale - image.clientHeight) / 2);
        this.pan.x = Math.max(-maxX, Math.min(maxX, this.pan.x));
        this.pan.y = Math.max(-maxY, Math.min(maxY, this.pan.y));
        image.style.transform = `translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.scale})`;
        this.controls.querySelector('[data-media-reset]').hidden = this.scale <= 1;
      }

      endGesture(event, cancelled = false) {
        if (!this.pointers.has(event.pointerId)) return;
        const start = this.gesture;
        const lastPointer = this.pointers.size === 1;
        this.pointers.delete(event.pointerId);
        if (this.content.hasPointerCapture(event.pointerId)) this.content.releasePointerCapture(event.pointerId);
        if (!cancelled && lastPointer && start && !start.pinched && this.scale === 1) {
          const dx = event.clientX - start.center.x;
          const dy = event.clientY - start.center.y;
          if (Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy) * 1.2) this.stepMedia(dx < 0 ? 1 : -1);
        }
        this.beginGesture();
      }
    }
  );
}
